import React, {useState, useRef} from "react";
import {X, Image as ImageIcon} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient} from "@tanstack/react-query";
import {Modal, ModalFooter, FormField, SwitchRow, inputClass} from "./SettingsUI";

const SalesChannelEditor = ({channel, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: channel ? channel.name : "",
        description: channel ? channel.description : "",
        active: channel ? channel.active : true,
    });
    const [imagePreview, setImagePreview] = useState(channel?.imageUrl || null);
    const [imageFile, setImageFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showNotification('error', 'Veuillez sélectionner un fichier image valide');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                showNotification('error', 'La taille de l\'image ne doit pas dépasser 5MB');
                return;
            }

            setImageFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setImagePreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImagePreview(null);
        setImageFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const triggerImageUpload = () => {
        fileInputRef.current?.click();
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = "Le nom du canal est requis";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Use FormData for file upload support
            const data = new FormData();
            data.append('name', formData.name);
            data.append('description', formData.description || '');
            data.append('active', formData.active ? 'true' : 'false');

            if (imageFile) {
                data.append('image', imageFile);
            }

            if (channel) {
                // Update existing channel - use POST with /update endpoint for image upload
                if (imageFile) {
                    await api.post(`/sales-channels/${channel.id}/update`, data, {
                        headers: {
                            "X-account-id": accountId.id,
                        },
                    });
                } else {
                    // Use PUT for JSON update without image
                    await api.put(`/sales-channels/${channel.id}`, {
                        name: formData.name,
                        description: formData.description,
                        active: formData.active,
                    }, {
                        headers: {
                            "X-account-id": accountId.id,
                            "Content-Type": "application/json",
                        },
                    });
                }
                showNotification("success", "Canal de vente mis à jour avec succès");
            } else {
                // Create new channel
                await api.post("/sales-channels", data, {
                    headers: {
                        "X-account-id": accountId.id,
                    },
                });
                showNotification("success", "Canal de vente créé avec succès");
            }

            queryClient.invalidateQueries({queryKey: ["salesChannels"]});
            onClose();
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification("error", `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={channel ? "Modifier le canal de vente" : "Nouveau canal de vente"}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="sales-channel-form" saving={submitting}
                                 label={channel ? "Enregistrer" : "Créer"}/>}
        >
            <form id="sales-channel-form" onSubmit={handleSubmit} className="space-y-4">
                {/* Image tile, as on the catalogue product forms */}
                <div className="flex flex-col items-center">
                    <div className="relative w-24 h-24">
                        {imagePreview ? (
                            <>
                                <img src={imagePreview} alt="Aperçu du canal" onClick={triggerImageUpload}
                                     className="w-24 h-24 rounded-lg object-cover shadow-sm cursor-pointer"/>
                                <button type="button" onClick={removeImage} aria-label="Supprimer l'image"
                                        className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-sm hover:bg-red-600 transition-colors">
                                    <X size={14}/>
                                </button>
                            </>
                        ) : (
                            <button type="button" onClick={triggerImageUpload}
                                    className="w-24 h-24 rounded-lg flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary transition-colors">
                                <ImageIcon className="w-6 h-6 text-gray-400 mb-1"/>
                                <span className="text-xs text-gray-500 dark:text-gray-400">Ajouter</span>
                            </button>
                        )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-2">Image du canal</span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                    />
                </div>

                <FormField id="name" label="Nom" required error={errors.name}>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="ex: Glovo, Uber Eats, En boutique..."
                    />
                </FormField>

                <FormField id="description" label="Description">
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        className={`${inputClass(false)} resize-none`}
                        placeholder="Description du canal de vente..."
                    />
                </FormField>

                <SwitchRow id="active" label="Actif" checked={formData.active}
                           onChange={(checked) => setFormData(prev => ({...prev, active: checked}))}/>
            </form>
        </Modal>
    );
};

export default SalesChannelEditor;

